import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SelectOption } from '../../../../types/selectOption.type';
import { KubitBomRow, KubitRawMaterialOption } from '../ArticleForm';

interface Props {
    /** Available raw-material (type-3) articles to pick from. */
    options: KubitRawMaterialOption[];
    /** Chosen BOM rows — each a raw material with a template quantity. */
    value: KubitBomRow[];
    /** Unit catalog, to resolve each raw material's unit label from its unitId. */
    units: SelectOption[];
    onChange: (next: KubitBomRow[]) => void;
}

// KuBIT bill-of-materials picker. Same two-panel shape as the production-process
// builder (searchable library on the left, chosen list on the right), but the
// right panel is a TABLE: each chosen raw material shows its name, an editable
// template quantity, and its own unit (read-only — the unit belongs to the
// material, only the quantity is per-article). This template feeds the offer BOM.
const KubitBomBuilder: React.FC<Props> = ({ options, value, units, onChange }) => {
    const intl = useIntl();
    const [q, setQ] = useState('');

    const selectedKeys = useMemo(() => new Set(value.map((r) => String(r.value))), [value]);
    const filtered = useMemo(() => {
        const ql = q.trim().toLowerCase();
        return options.filter((o) => !ql || (o.label || '').toLowerCase().includes(ql));
    }, [options, q]);

    const unitLabel = (row: KubitBomRow): string => {
        const u = units.find((x) => String(x.value) === String(row.unitId));
        return String(u?.label ?? row.unitLabel ?? '—');
    };

    const add = (opt: KubitRawMaterialOption) => {
        if (selectedKeys.has(String(opt.value))) return;
        onChange([
            ...value,
            {
                value: opt.value,
                label: opt.label,
                quantity: 1,
                unitId: opt.unitId ?? null,
                unitLabel: opt.unitLabel,
                consumeResponsibilities: opt.consumeResponsibilities,
            },
        ]);
    };

    const setQty = (idx: number, v: string) =>
        onChange(value.map((r, i) => (i === idx ? { ...r, quantity: v === '' ? '' : Number(v) } : r)));

    const remove = (idx: number) => onChange(value.filter((_, i) => i !== idx));

    const searchPlaceholder = intl.formatMessage({
        id: 'admin.articles.form.kubitBom.addPlaceholder',
        defaultMessage: 'Shto lëndë të parë...',
    });

    return (
        <div className="art-builder">
            {/* ── Library — available raw-material articles ─────────────────── */}
            <div className="art-builder__panel">
                <div className="art-builder__head">
                    <span className="art-builder__title">
                        <FormattedMessage id="admin.articles.form.kubitBom.available" defaultMessage="Lëndët e para të disponueshme" />
                    </span>
                    <span className="art-builder__count">{filtered.length}</span>
                </div>

                <div className="art-builder__search">
                    <svg className="art-builder__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="11" cy="11" r="7" />
                        <path d="m20 20-3.2-3.2" />
                    </svg>
                    <input
                        type="text"
                        placeholder={searchPlaceholder}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        aria-label={searchPlaceholder}
                    />
                </div>

                <div className="art-builder__lib">
                    {filtered.length === 0 ? (
                        <div className="art-builder__lib-empty">
                            <FormattedMessage id="admin.articles.form.kubitBom.noResults" defaultMessage="Asnjë lëndë e parë. Krijoji si artikull 'Lëndë e parë'." />
                        </div>
                    ) : (
                        filtered.map((p) => {
                            const added = selectedKeys.has(String(p.value));
                            return (
                                <div
                                    key={p.value}
                                    className={`art-builder__item ${added ? 'is-added' : ''}`}
                                    role="button"
                                    tabIndex={added ? -1 : 0}
                                    onClick={() => !added && add(p)}
                                    onKeyDown={(e) => {
                                        if (!added && (e.key === 'Enter' || e.key === ' ')) {
                                            e.preventDefault();
                                            add(p);
                                        }
                                    }}
                                >
                                    <span className="art-builder__item-name">{p.label}</span>
                                    <span className="art-builder__item-add" aria-hidden="true">
                                        {added ? (
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M5 12.5l4.5 4.5L19 7" />
                                            </svg>
                                        ) : (
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M12 5v14M5 12h14" />
                                            </svg>
                                        )}
                                    </span>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* ── Chosen — BOM table (name | quantity | unit read-only | remove) ─ */}
            <div className="art-builder__panel art-builder__panel--seq">
                <div className="art-builder__head">
                    <svg className="art-builder__head-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M3 6h18M3 12h18M3 18h18" />
                    </svg>
                    <span className="art-builder__title">
                        <FormattedMessage id="admin.articles.form.kubitBom.selectedTitle" defaultMessage="Përbërësit e zgjedhur" />
                    </span>
                    <span className="art-builder__count art-builder__count--brand">{value.length}</span>
                </div>

                <div className="art-builder__seq">
                    {value.length === 0 ? (
                        <div className="art-builder__seq-empty">
                            <div className="art-builder__seq-empty-title">
                                <FormattedMessage id="admin.articles.form.kubitBom.emptyHint" defaultMessage="Asnjë lëndë e parë e zgjedhur." />
                            </div>
                            <div className="art-builder__seq-empty-sub">
                                <FormattedMessage id="admin.articles.builder.emptySub" defaultMessage="Tërhiq ose kliko nga lista" />
                            </div>
                        </div>
                    ) : (
                        <div className="art-bom__rows">
                            <div className="art-bom__head">
                                <span><FormattedMessage id="admin.articles.form.bom.material" defaultMessage="Materiali" /></span>
                                <span><FormattedMessage id="admin.articles.form.bom.quantity" defaultMessage="Sasia / njësi" /></span>
                                <span><FormattedMessage id="admin.articles.form.bom.unit" defaultMessage="Njësia" /></span>
                                <span aria-hidden="true" />
                            </div>
                            {value.map((row, idx) => (
                                <div className="art-bom__row" key={String(row.value)}>
                                    <span className="art-bom__name">{row.label}</span>
                                    <input
                                        type="number"
                                        min={0}
                                        step="0.0001"
                                        className="art-form-field__input mono art-bom__qty"
                                        value={row.quantity}
                                        onChange={(e) => setQty(idx, e.target.value)}
                                        // A BOM line needs a positive quantity. Rather than let the
                                        // backend silently clamp 0/empty to 1, normalise on blur so
                                        // what the user sees always matches what gets saved.
                                        onBlur={(e) => { if (e.target.value === '' || !(Number(e.target.value) > 0)) setQty(idx, '1'); }}
                                        aria-label={intl.formatMessage({ id: 'admin.articles.form.bom.quantity', defaultMessage: 'Sasia / njësi' })}
                                    />
                                    <span className="art-kbom__unit" title={unitLabel(row)}>{unitLabel(row)}</span>
                                    <button
                                        type="button"
                                        className="art-bom__remove"
                                        onClick={() => remove(idx)}
                                        aria-label={intl.formatMessage({ id: 'common.remove', defaultMessage: 'Hiq' })}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M6 6l12 12M18 6 6 18" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default KubitBomBuilder;
